import NotFound from "@/assets/images/not-found.png";
import CustomTooltip from "@/components/CustomTooltip";
import { getUnicornTimeSeries } from "@/services/api/UnicornTracker";
import { Info } from "@phosphor-icons/react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import Image from "next/image";
import { useSearchParams } from "next/navigation";
import React, { useEffect, useState } from "react";

export default function UnicornCountValuation({
  unicornStats,
  regionData,
  countryData,
}) {
  const [timeSeriesData, setTimeSeriesData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regionName, setRegionName] = useState("");
  const [unicornBirths, setUnicornBirths] = useState(null);

  const searchParams = useSearchParams();
  const selectedRegionId = searchParams.get("unicorn_regions");
  const selectedCountryId = searchParams.get("country");
  const sectors = searchParams.get("sectors");
  const stages = searchParams.get("stages");
  const valuationCondition = searchParams.get("valuation_condition");
  const valuationAmount = searchParams.get("valuation_amount");
  const yearFoundedCondition = searchParams.get("year_founded_condition");
  const yearFoundedYear = searchParams.get("year_founded_year");
  const timeToUnicornMin = searchParams.get("min_time_to_unicorn");
  const timeToUnicornMax = searchParams.get("max_time_to_unicorn");
  const unicornStatus = searchParams.get("unicorn_status");
  useEffect(() => {
    async function fetchTimeSeriesData() {
      try {
        setLoading(true);

        const params = {
          period: "yearly",
          years: 5,
          metric: "both",
        };

        // Add region/country filters if selected
        if (selectedRegionId) {
          params.unicorn_regions = selectedRegionId;
        }
        if (selectedCountryId) {
          params.countries = selectedCountryId;
        }

        // Add additional filters
        if (sectors) {
          params.sectors = sectors;
        }
        if (stages) {
          params.stages = stages;
        }
        if (valuationCondition && valuationAmount) {
          params.valuation_condition = valuationCondition;
          params.valuation_amount = valuationAmount;
        }
        if (yearFoundedCondition && yearFoundedYear) {
          params.year_founded_condition = yearFoundedCondition;
          params.year_founded_year = yearFoundedYear;
        }
        if (timeToUnicornMin) {
          params.min_time_to_unicorn = timeToUnicornMin;
        }
        if (timeToUnicornMax) {
          params.max_time_to_unicorn = timeToUnicornMax;
        }

        const statuses = unicornStatus
          ? unicornStatus.split(",").filter(Boolean)
          : ["current", "fallen", "alumni"];
        params.unicorn_status = statuses.join(",");
        if (statuses.includes("fallen")) params.include_lost_unicorn = 1;
        if (statuses.includes("alumni")) params.include_alumni = 1;
        if (!statuses.includes("current")) params.exclude_current_unicorn = 1;

        const response = await getUnicornTimeSeries({ params });
        const timeSeries = response.data?.data?.time_series || [];

        // Extract region/country name from API response or props
        let extractedName = "";

        // Check API response structure
        if (response.data?.data?.country?.name) {
          extractedName = response.data.data.country.name;
        } else if (response.data?.data?.region?.name) {
          extractedName = response.data.data.region.name;
        } else if (response.data?.country?.name) {
          extractedName = response.data.country.name;
        } else if (response.data?.region?.name) {
          extractedName = response.data.region.name;
        }

        // Fallback to props if API doesn't provide name
        if (!extractedName) {
          if (countryData?.name) {
            extractedName = countryData.name;
          } else if (regionData?.name) {
            extractedName = regionData.name;
          }
        }

        setRegionName(extractedName);

        // Extract unicorn births last 12 months
        if (response.data?.data?.unicorn_births_last_12_months !== undefined) {
          setUnicornBirths(response.data.data.unicorn_births_last_12_months);
        } else {
          setUnicornBirths(null);
        }

        setTimeSeriesData(timeSeries);
      } catch (error) {
        console.error("Error fetching time series data:", error);
        setTimeSeriesData(null);

        // Set region name from props when API fails
        if (countryData?.name) {
          setRegionName(countryData.name);
        } else if (regionData?.name) {
          setRegionName(regionData.name);
        } else {
          setRegionName("");
        }
      } finally {
        setLoading(false);
      }
    }

    fetchTimeSeriesData();
  }, [
    selectedRegionId,
    selectedCountryId,
    regionData,
    countryData,
    sectors,
    stages,
    valuationCondition,
    valuationAmount,
    yearFoundedCondition,
    yearFoundedYear,
    timeToUnicornMin,
    timeToUnicornMax,
    unicornStatus,
  ]);

  // Separate effect to handle region name from props
  useEffect(() => {
    if (countryData?.name) {
      setRegionName(countryData.name);
    } else if (regionData?.name) {
      setRegionName(regionData.name);
    } else if (!selectedRegionId && !selectedCountryId) {
      setRegionName("");
    }
  }, [regionData, countryData, selectedRegionId, selectedCountryId]);

  // Generate chart data from API response
  const generateChartData = () => {
    if (timeSeriesData && timeSeriesData.length > 0) {
      const currentYear = new Date().getFullYear().toString();
      const categories = timeSeriesData.map((item) => {
        const period = String(item.period ?? "");
        return period === currentYear ? `${period} (YTD)` : period;
      });
      const yearlyNew = timeSeriesData.map((item) => item.unicorn_count);
      const cumulativeCounts = timeSeriesData.map((item) =>
        Number(item.cumulative_count || item.unicorn_count || 0),
      );
      const baseCounts = cumulativeCounts.map((count, index) =>
        Math.max(0, count - (yearlyNew[index] || 0)),
      );
      const valuations = timeSeriesData.map((item) =>
        Math.round(
          ((item.cumulative_valuation ?? item.total_valuation ?? 0) /
            1_000_000_000) *
            10,
        ) / 10,
      );
      return { categories, newCounts: yearlyNew, baseCounts, valuations };
    }

    return { categories: [], newCounts: [], baseCounts: [], valuations: [] };
  };

  const { categories, newCounts, baseCounts, valuations } = generateChartData();

  // Check if we have any data to display
  const hasData = newCounts.length > 0 && valuations.length > 0;
  const options = {
    chart: {
      backgroundColor: "transparent",
    },
    title: {
      text: null,
    },
    xAxis: {
      categories: categories,
      labels: {
        style: { fontSize: "11px", color: "#fff", opacity: 0.5 },
      },
      lineColor: "transparent",
      tickColor: "transparent",
    },
    yAxis: [
      {
        title: {
          text: "Cumulative Unicorns",
          style: { fontSize: "11px", color: "#fff", opacity: 0.5 },
        },
        labels: { style: { fontSize: "11px", color: "#fff", opacity: 0.3 } },
        gridLineColor: "rgba(255,255,255,0.1)",
      },
      {
        title: {
          text: "Total Valuation ($B)",
          style: { fontSize: "11px", color: "#fff", opacity: 0.5 },
        },
        labels: { style: { fontSize: "11px", color: "#fff", opacity: 0.3 } },
        opposite: true,
        gridLineWidth: 0,
      },
    ],
    legend: {
      itemStyle: { fontSize: "11px", color: "#fff", opacity: 0.3 },
      itemHoverStyle: { color: "#fff", opacity: 1 },
    },
    tooltip: {
      shared: true,
      formatter: function () {
        const base = this.points.find(
          (p) => p.series.name === "Cumulative Unicorns",
        );
        const accent = this.points.find(
          (p) => p.series.name === "New This Year",
        );
        const valuation = this.points.find(
          (p) => p.series.name === "Total Valuation ($B)",
        );
        const cumulative = (base?.y || 0) + (accent?.y || 0);
        let html = `<b>${this.x}</b><br/>`;
        html += `<span style="color:#8a2be2">●</span> Cumulative Total: <b>${cumulative}</b><br/>`;
        if (accent) html += `<span style="color:#c084fc">●</span> New This Year: <b>${accent.y}</b><br/>`;
        if (valuation) {
          html += `<span style="color:orange">●</span> Total Valuation ($B): <b>$${valuation.y}B</b><br/>`;
        }
        return html;
      },
    },
    plotOptions: {
      column: {
        stacking: "normal",
        borderWidth: 0,
        pointWidth: 30,
      },
    },
    series: [
      {
        name: "Cumulative Unicorns",
        type: "column",
        yAxis: 0,
        data: baseCounts,
        color: "rgba(138, 43, 226, 0.25)",
      },
      {
        name: "New This Year",
        type: "column",
        yAxis: 0,
        data: newCounts,
        color: {
          linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
          stops: [
            [0, "#c084fc"],
            [1, "#8a2be2"],
          ],
        },
      },
      {
        name: "Total Valuation ($B)",
        type: "line",
        yAxis: 1,
        data: valuations,
        color: "orange",
        marker: { enabled: true, radius: 5 },
        tooltip: {
          pointFormatter: function () {
            return `<span style="color:${this.color}">●</span> ${this.series.name}: <b>$${this.y}B</b><br/>`;
          },
        },
      },
    ],
  };

  return (
    <>
      <div className='flex items-center justify-between gap-1.5'>
        <p className='text-base font-medium sm:text-sm'>
          Unicorn Growth
        </p>
        <CustomTooltip
          trigger={
            <span className='-mt-2 -mr-2 text-white/40'>
              <Info className='size-5' />
            </span>
          }
          content="Cumulative unicorns by year for your current filters, with total private valuation overlaid. Valuation is each company's latest known private value, summed. Based on current HQ."
        />
      </div>

      {/* KPI Display */}
      {/* {unicornBirths !== null && (
        <div className='mt-1'>
          <p className='text-xs opacity-40'>Unicorn Births (Last 12 Months)</p>
          <p className='mt-1 text-3xl font-bold text-white'>{unicornBirths}</p>
        </div>
      )} */}
      {hasData ? (
        <div className='mt-4 -mx-4'>
          <HighchartsReact highcharts={Highcharts} options={options} />
        </div>
      ) : (
        <div className='p-6 py-4 my-4 text-center border border-dashed rounded-lg'>
          <div className='relative block mx-auto aspect-square size-20'>
            <Image
              objectFit='cover'
              src={NotFound.src}
              alt='NotFound Image'
              layout='fill'
              className='object-cover'
            />
          </div>
          <p className='mt-3 text-base font-semibold'>Oops, No Data :(</p>
          <p className='mt-1 text-xs text-white/40'>No chart data available</p>
        </div>
      )}
    </>
  );
}
